import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { Card } from 'react-bootstrap';
import apiAxios from '../../../config/ApiAxios';
import { useQuery } from '../../../utils/useQuery';
import { useHistory, useLocation } from 'react-router-dom';
import { handleGraficosParams } from '../../../utils/handleGraficosParams';
import DatePicker, { registerLocale } from 'react-datepicker';
import { parseDate } from '../../../utils/dateFormatter';
import { es } from 'date-fns/locale';
import { DesdeButton, HastaButon } from '../DesdeYHastaButtons';
import { globalEndDate, globalStartDate } from '../../../config/reportesGlobales';
import { FormatterNumber } from '../../../utils/formatter';

registerLocale('es', es);

const ComportamientoPagosTorta = () => {
  const [indexTipoDato, setIndexTipoDato] = useState(0);

  // para pie necesitamos un array de números; lo guardo como [{name,data}] porque ya lo usabas así,
  // pero al Chart le paso solo el .data
  const [series, setSeries] = useState([
    { name: 'Pagos', data: [] },
  ]);

  const [startDate, setStartDate] = useState(globalStartDate);
  const [endDate, setEndDate] = useState(globalEndDate);
  const location = useLocation();

  const [pieLabels, setPieLabels] = useState([]); // <- inicia vacío, no undefined
  const [pieData, setPieData] = useState({});     // <- inicia objeto, no undefined

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fechaInicio = searchParams.get('fecha-inicio');
    const fechaFinal = searchParams.get('fecha-final');

    if (fechaInicio) setStartDate(parseDate(fechaInicio));
    if (fechaFinal) setEndDate(parseDate(fechaFinal));
  }, [location.search]);

  const startDateYear = startDate.getFullYear();
  const startDateMonth = String(startDate.getMonth() + 1).padStart(2, '0');
  const startDateDay = String(startDate.getDate()).padStart(2, '0');

  const endDateYear = endDate.getFullYear();
  const endDateMonth = String(endDate.getMonth() + 1).padStart(2, '0');
  const endDateDay = String(endDate.getDate()).padStart(2, '0');

  // OBTENER CRÉDITOS
  useEffect(() => {
    const obtenerCreditos = async () => {
      try {
        const apiResponse = await apiAxios.get(
          `/reportes/global/pagos/comportamiento?fecha_desde=${startDateYear}-${startDateMonth}-${startDateDay}&fecha_hasta=${endDateYear}-${endDateMonth}-${endDateDay}&agrupar_por[]=comportamiento`
        );

        if (apiResponse?.status === 200) {
          const data = apiResponse.data.filter(d => d.importe_pago !== null);

          const labels = data.map(item => item.comportamiento);
          const seriesData = data.map(item => parseFloat(item.importe_pago));

          const pieInfo = data.reduce((acc, item) => {
            acc[item.comportamiento] = {
              importe: parseFloat(item.importe_pago),
              cantidad: Number(item.cantidad_cuotas) || 0, // <- AQUÍ
            };
            return acc;
          }, {});

          setPieLabels(labels);
          setPieData(pieInfo);
          setSeries([{ name: 'Pagos', data: seriesData }]);
        }
      } catch (e) {
        console.log(e);
      }
    };

    obtenerCreditos();

    // No dejamos series en [{}] para evitar accesos indefinidos
    // return () => setSeries([{ name: 'Pagos', data: [] }]);
  }, [endDateDay, endDateMonth, endDateYear, startDateDay, startDateMonth, startDateYear]);

  const colors = [
    { backgroundColor: ['#008FFB', '#0019FB', '#00F8FB', '#5D00FB', '#925BF0'] },
    { backgroundColor: ['#00e396', '#05A06B', '#90F8C9', '#00430E', '#B3E174'] },
    { backgroundColor: ['#feb019', '#FE1972', '#BE974D', '#810508', '#EF7B7E'] },
  ];

  const options = {
    chart: {
      width: '100%',
      height: '100%',
      type: 'pie',
      toolbar: { show: true },
    },
    legend: { position: 'bottom' },
    plotOptions: { pie: { customScale: 1.0 } },
    labels: pieLabels,
    tooltip: {
      y: {
        // val es el valor numérico de la porción (importe)
        formatter: function (val, opts) {
          // En pie/donut el slice es dataPointIndex (NO seriesIndex).
          const i = opts?.dataPointIndex;
          if (i == null || i < 0) return '';
          // Tomo el label desde Apex (más robusto) o desde estado.
          const label = opts?.w?.globals?.labels?.[i] ?? pieLabels?.[i];
          const info = pieData?.[label] || { importe: val, cantidad: 0 };
          const importe = FormatterNumber(info.importe ?? val);
          return `Importe: ${importe} | Cuotas: ${info.cantidad}`;
        },
      },
    },
    colors: colors[indexTipoDato].backgroundColor,
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: { width: 200 },
          legend: { position: 'bottom' },
        },
      },
    ],
  };

  const query = useQuery();
  const history = useHistory();

  const ocultarGrafico = query.get('ocultarGrafico');
  const ocultarGraficoArray = ocultarGrafico
    ? ocultarGrafico.replace(/[[\]]/g, '').split(',').map(Number)
    : [];

  return (
    <>
      <style>
        {`
          #a-cobrar-dropdown::before {
            content: '';
            height: 10px;
            width: 10px;
            background-color: ${colors[indexTipoDato].backgroundColor[0]};
            display: inline-block;
            margin-right: 5px;
          }
        `}
      </style>

      <div
        className="column-card"
        style={{ display: ocultarGraficoArray.includes(8) ? 'none' : 'block' }}
      >
        <div className="px-2 py-2 border border-bottom-0 d-flex align-items-center gap-1">
          <DatePicker
            locale="es"
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            dateFormat="dd/MM/yyyy"
            customInput={<DesdeButton className="" />}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            // Idealmente usar Date en max/min:
            // minDate={new Date(2010,0,1)}
            // maxDate={new Date(2040,0,1)}
          />
          <i className="bi bi-arrow-right"></i>
          <DatePicker
            locale="es"
            selectsEnd
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            dateFormat="dd/MM/yyyy"
            customInput={<HastaButon className="me-3" />}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            // maxDate={new Date(2040,0,1)}
          />
        </div>

        <Card className="custom-card-body">
          <Card.Body>
            <Card.Title className="d-flex justify-content-between">
              <span>
                Comportamiento de Pago (Torta){' '}
                <button
                  title="Ocultar"
                  className="btn btn-light ms-2"
                  onClick={() => handleGraficosParams(8, ocultarGraficoArray, history)}
                >
                  <i className="bi bi-eye"></i>
                </button>
                <a title="Muestra la distribución....." className="btn btn-light ms-2" disabled>
                  <i className="bi bi-info-circle"></i>
                </a>
              </span>
            </Card.Title>

            <div className="mx-auto">
              <Chart
                options={options}
                series={series[0]?.data ?? []}
                type="pie"
                width="100%"
              />
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default ComportamientoPagosTorta;
